
import { useEffect, useMemo, useRef, useState } from "react";
import {
  XMarkIcon,
  UsersIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  NoSymbolIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
  MicrophoneIcon,
  ArrowPathIcon,
  SignalIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import {
  Chart,
  ArcElement,
  Tooltip,
  PieController,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Select } from "V2.0/common/select";
import useBulkCampaignStatistics from "../useBulkCampaignStatistics";
import { BULK_CAMPAIGN_STATUS } from "../../BulkCampaignStatus";

Chart.register(
  ArcElement,
  Tooltip,
  PieController,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
);

/*
 * Field maps, one per campaignType. The response itself carries no campaignType, so it comes
 * from the campaign row instead.
 *
 * The backend stores only two sets of counters — callCampaignDetails and chatCampaignDetails —
 * and routes sms, whatsapp and email alike to the chat one. So those three read the identical
 * field keys and differ only in wording and iconography; there are no per-channel counters to
 * show (chatCampaignDetails has no opens, clicks or bounces, so an email report cannot offer
 * them however much it would want to). chatShape() below builds the three from one definition
 * so the shared keys cannot drift apart while the labels vary.
 *
 * The pie partitions totalContacts by outcome — `slices` below, plus a residual for
 * whatever those don't account for. Only per-contact outcomes may go in it: every
 * other figure counts messages or attempts rather than people, so it cannot partition a
 * contact count. The clearest case is messagesSent, incremented both at dispatch and again
 * on the provider's delivered webhook — real data shows 15 sent against 9 contacts. Those
 * all live in `cards`. callsAttempted is out for the same reason (76 attempts / 45 contacts
 * in real data, since a contact is retried).
 *
 * Slice hues are the first two all-pairs-safe categorical slots, validated together for
 * colour-vision separation (worst pair ΔE 8.9 protan, above the 8 target; normal-vision
 * ΔE 23.8) — a pie is compared across, not just between neighbours, so all pairs have to
 * hold. Both sit under 3:1 against white, so the legend carries printed values: identity
 * and magnitude never rest on hue alone. The residual is deliberately a recessive gray —
 * it is an absence of outcome, not another category.
 */
const SLICE_COLORS = ["#10b981", "#f59e0b"];
const RESIDUAL_COLOR = "#cbd5e1";

/*
 * One shape for each of sms / whatsapp / email. Only `channelName` and `channelIcon` vary — the
 * counter keys are shared because the backend writes all three into chatCampaignDetails.
 *
 * Card labels stay single words rather than "Messages sent" / "Emails sent": a stat card is
 * ~113px wide once the icon and padding are taken out, so a two-word label truncates. The
 * channel is carried by the icon and the header badge instead, where there is room for it.
 */
function chatShape(channelName: string, channelIcon: any) {
  return {
    channelName,
    // usersResponded is the only per-contact figure here; everything else counts messages, and
    // messagesSent is incremented twice per message (dispatch, then the delivered webhook), so
    // none of them can partition a contact count.
    slices: [{ keys: ["usersResponded"], name: "Replied" }],
    residualName: "No reply",
    // Same backend field as the call rate (usersResponded / messagesSent), but for these
    // channels it is a reply rate — "connectivity" would read as a delivery figure.
    rateLabel: "Reply Rate",
    showsDuration: false,
    cards: [
      { key: "messagesAttempted", label: "Attempted", icon: channelIcon, tone: "neutral" },
      { key: "messagesSent", label: "Sent", icon: CheckCircleIcon, tone: "good" },
      { key: "messagesFailed", label: "Failed", icon: XCircleIcon, tone: "bad" },
      { key: "messagesBlocked", label: "Blocked", icon: NoSymbolIcon, tone: "bad" },
    ],
  };
}

const SHAPES = {
  call: {
    /*
     * Answered vs not answered, so a slice can span more than one backend counter.
     * Voicemail sits on the not-answered side: reaching a machine is not reaching the
     * person, and it matches how the backend computes connectivityRate, which keeps
     * voicemail out of the numerator but inside the denominator.
     *
     * Caveat worth knowing when these don't reconcile: callsCompleted is capped at one per
     * contact by the Campaign guard on the completion path, but callsNotAnswered and
     * voicemail are incremented per *attempt* with no such guard, so a contact retried four
     * times and never reached adds four. They line up with totalContacts only while the
     * report reflects a single attempt per contact — which is the usual case, because the
     * backend freezes campaignReport once no Campaign row is left in a non-terminal status,
     * and retries then continue against the live counters the frozen snapshot no longer
     * shows. `overCounted` below is what catches the case where they don't line up.
     */
    slices: [
      { keys: ["callsCompleted"], name: "Answered" },
      { keys: ["callsNotAnswered", "voicemail"], name: "Not answered" },
    ],
    // Covers callsFailed, callsBlocked, and contacts not yet dialled on a live campaign.
    residualName: "Other",
    rateLabel: "Connectivity Rate",
    channelName: "Call",
    // Only voice campaigns accrue minutes; the other channels always report 0, so the tile
    // would be dead weight there.
    showsDuration: true,
    // Unanswered + Voicemail are the two components of the "Not answered" slice (16 + 2 = 18
    // in real data), kept as cards because merging them into one arc is otherwise the only
    // place either number appears — and they prompt different actions.
    cards: [
      { key: "callsAttempted", label: "Attempted", icon: PhoneArrowUpRightIcon, tone: "neutral" },
      { key: "callsNotAnswered", label: "Unanswered", icon: PhoneXMarkIcon, tone: "neutral" },
      { key: "voicemail", label: "Voicemail", icon: MicrophoneIcon, tone: "neutral" },
      { key: "callsFailed", label: "Failed", icon: XCircleIcon, tone: "bad" },
      { key: "callsBlocked", label: "Blocked", icon: NoSymbolIcon, tone: "bad" },
    ],
  },
  sms: chatShape("SMS", DevicePhoneMobileIcon),
  whatsapp: chatShape("WhatsApp", ChatBubbleLeftRightIcon),
  email: chatShape("Email", EnvelopeIcon),
};

/*
 * Anything not in SHAPES falls back to a generic message shape rather than throwing or rendering
 * a blank report. That mirrors the backend, which sends chatCampaignDetails for every
 * campaignType except "call" — so a channel added server-side before this map is updated still
 * reads the right counters, just with generic wording.
 */
const FALLBACK_SHAPE = chatShape("Message", PaperAirplaneIcon);

function shapeFor(campaignType: string) {
  return SHAPES[campaignType as keyof typeof SHAPES] || FALLBACK_SHAPE;
}

type Tone = "good" | "bad" | "neutral";

const toneStyles: Record<Tone, string> = {
  good: "text-emerald-600 bg-emerald-50",
  bad: "text-red-600 bg-red-50",
  neutral: "text-slate-600 bg-slate-100",
};

// The `tone` on a card descriptor widens to string, so it cannot index toneStyles directly.
// Falling back to neutral keeps an unrecognised tone looking like a normal card instead of
// emitting className="undefined" and dropping the icon's background entirely.
function toneStyle(tone: string) {
  return toneStyles[tone as Tone] || toneStyles.neutral;
}

// Terminal-but-unhappy states read as failures; Ongoing/Paused mean the numbers are still moving.
const statusStyles = {
  Completed: "text-emerald-700 bg-emerald-50 ring-emerald-600/20",
  Ongoing: "text-blue-700 bg-blue-50 ring-blue-600/20",
  Processing: "text-blue-700 bg-blue-50 ring-blue-600/20",
  Scheduled: "text-slate-700 bg-slate-100 ring-slate-600/20",
  Paused: "text-amber-700 bg-amber-50 ring-amber-600/20",
  OutsideOfficeHours: "text-amber-700 bg-amber-50 ring-amber-600/20",
  QuotaExhausted: "text-amber-700 bg-amber-50 ring-amber-600/20",
  Failed: "text-red-700 bg-red-50 ring-red-600/20",
  Cancelled: "text-red-700 bg-red-50 ring-red-600/20",
  TelephonyFailure: "text-red-700 bg-red-50 ring-red-600/20",
};

function formatDateRange(startTime, endTime) {
  const opts = { month: "short", day: "numeric", year: "numeric" } as const;
  const start = startTime ? new Date(startTime) : null;
  const end = endTime ? new Date(endTime) : null;

  if (start && end) {
    return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
  }
  if (start) return start.toLocaleDateString(undefined, opts);
  return "";
}

/*
 * connectivityRate arrives as a percentage (0–100), not a fraction, and can be a long float
 * from the division behind it. Anything non-numeric renders as an em dash rather than "0%",
 * which would claim nobody connected when the truth is that the backend sent nothing.
 */
function formatRate(rate) {
  if (typeof rate !== "number" || !Number.isFinite(rate)) return "—";
  return `${Number(rate.toFixed(1)).toLocaleString()}`;
}

type TBreakdownEntry = { name: string; color: string; value: number };

function PieChart({ breakdown }: { breakdown: TBreakdownEntry[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart<"pie"> | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const total = breakdown.reduce((sum, entry) => sum + entry.value, 0);

    chartRef.current = new Chart(canvasRef.current, {
      type: "pie",
      data: {
        labels: breakdown.map((b) => b.name),
        datasets: [
          {
            data: breakdown.map((b) => b.value),
            backgroundColor: breakdown.map((b) => b.color),
            // A 2px gap in the surface colour, not a stroke — touching arcs need white
            // between them to read as separate.
            borderColor: "#ffffff",
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a",
            padding: 8,
            titleFont: { size: 12, weight: 500 },
            bodyFont: { size: 12, weight: 500 },
            displayColors: false,
            callbacks: {
              // Share is the point of a part-to-whole chart and an arc angle can't be read
              // off precisely, so the tooltip states it rather than leaving it to be judged.
              label: (ctx) => {
                const share = total > 0 ? Math.round((ctx.parsed / total) * 100) : 0;
                return `${ctx.label}: ${ctx.parsed.toLocaleString()} (${share}%)`;
              },
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [breakdown]);

  return <canvas ref={canvasRef} />;
}

/*
 * Responses per attempt. A single series, so it carries no legend — the heading names it — and
 * one hue rather than a colour per bar: colour here would encode nothing that the position on
 * the axis doesn't already say. #6366f1 clears 3:1 against the card surface, so the bars stay
 * legible without printing a number on each one.
 *
 * The number of attempts is open-ended (it follows the workspace's follow-up settings), which
 * is why this is a chart and not a row of cards. Past the point where bars would get too thin
 * to compare, the strip scrolls sideways instead of compressing — see MIN_BAR_SLOT.
 */
const BAR_COLOR = "#6366f1";

// Floor on the horizontal space each attempt gets. Below roughly this, a 24px bar plus its
// axis label stops being readable, so the container scrolls rather than squeezing further.
const MIN_BAR_SLOT = 44;

function AttemptBarChart({ attempts }: { attempts: { label: string; value: number }[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart<"bar"> | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: attempts.map((a) => a.label),
        datasets: [
          {
            data: attempts.map((a) => a.value),
            backgroundColor: BAR_COLOR,
            // Capped rather than filling its slot: a bar that fills the band reads as a block
            // of colour, and the leftover space is what separates neighbours.
            maxBarThickness: 24,
            // Rounded at the growing end only. A radius on the baseline corners would lift the
            // bar off the axis it is measured from.
            borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a",
            padding: 8,
            titleFont: { size: 12, weight: 500 },
            bodyFont: { size: 12, weight: 500 },
            displayColors: false,
            callbacks: {
              // No share here on purpose. These sum to the live responder count, which can
              // differ from the frozen figure the pie shows, so a percentage would be measured
              // against a denominator the rest of the report doesn't display.
              label: (ctx) => `${ctx.parsed.y.toLocaleString()} responded`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 11 }, color: "#64748b" },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#f1f5f9" },
            border: { display: false },
            ticks: {
              // Response counts are whole people; without this Chart.js can offer 2.5.
              precision: 0,
              maxTicksLimit: 4,
              font: { size: 11 },
              color: "#94a3b8",
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [attempts]);

  return <canvas ref={canvasRef} />;
}

function Shell({ channel, title, subtitle, status, onClose, children, footer }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {channel} Campaign Report
            </p>
            <div className="mt-1 flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
              {status && (
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                    statusStyles[status] || statusStyles.Scheduled
                  }`}
                >
                  {status}
                </span>
              )}
            </div>
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
}

function BulkCampaignReportModal(props) {
  const { isOpen, onClose, workspaceId, bulkCampaign, onExport } = props;

  const bulkCampaignId = bulkCampaign?._id;

  const {
    statistics,
    isStatisticsLoading,
    statisticsError,
    fetchBulkCampaignStatistics,
  } = useBulkCampaignStatistics(workspaceId, bulkCampaignId);

  useEffect(() => {
    if (isOpen && workspaceId && bulkCampaignId) {
      fetchBulkCampaignStatistics();
    }
  }, [isOpen, workspaceId, bulkCampaignId, fetchBulkCampaignStatistics]);

  /*
   * Which extracted field the breakdown below is showing. Null means "not chosen yet", which
   * resolves to the first available field at render — deliberately not seeded with a field
   * name, because the field list isn't known until the request lands, and a different campaign
   * opened from the same list has entirely different fields. The choice is validated against
   * the current list on every render (see activeField) rather than reconciled in an effect, so
   * a selection that no longer exists falls back instead of rendering an empty breakdown.
   */
  const [selectedField, setSelectedField] = useState<string | null>(null);

  // The statistics response has no campaignType; only the campaign row does.
  const shape = shapeFor(bulkCampaign?.campaignType);

  // One slice per outcome, plus a residual so the pie always totals totalContacts — the
  // number printed beneath it.
  const { breakdown, overCounted } = useMemo(() => {
    const stats = statistics?.campaignStatistics || {};
    const totalContacts = statistics?.totalContacts || 0;

    // A slice can span several backend counters — "Not answered" is callsNotAnswered plus
    // voicemail — so the value is the sum of its keys rather than a single lookup.
    const slices = shape.slices.map(({ keys, name }, index) => ({
      name,
      color: SLICE_COLORS[index],
      value: keys.reduce((sum, key) => sum + (stats[key] || 0), 0),
    }));

    const accounted = slices.reduce((sum, slice) => sum + slice.value, 0);
    const residual = totalContacts - accounted;

    // A zero residual is dropped rather than drawn: Chart.js renders a 0-value arc as an
    // invisible slice that still takes a legend row and a tooltip, which reads as a missing
    // segment. Answered + not answered sums to exactly totalContacts whenever nothing failed
    // and nothing is pending, so this is the common case, not an edge one.
    return {
      breakdown:
        residual > 0
          ? [...slices, { name: shape.residualName, color: RESIDUAL_COLOR, value: residual }]
          : slices,
      // Outcome counts can exceed the contact count — usersResponded tallies inbound replies
      // rather than people, and a call retried after voicemail can land in two buckets. The
      // flag lets the UI admit the figures don't reconcile instead of quietly misleading.
      overCounted: residual < 0,
    };
  }, [statistics, shape]);

  const ringTotal = breakdown.reduce((sum, entry) => sum + entry.value, 0);

  if (!isOpen) return null;

  const title = bulkCampaign?.name || "Bulk Campaign";
  const subtitle = formatDateRange(bulkCampaign?.startTime, bulkCampaign?.endTime);

  const closeButton = (
    <button
      onClick={onClose}
      className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
    >
      Close
    </button>
  );

  if (isStatisticsLoading) {
    return (
      <Shell
        channel={shape.channelName}
        title={title}
        subtitle={subtitle}
        status={null}
        onClose={onClose}
        footer={closeButton}
      >
        <div className="flex h-56 items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Loading report…
          </div>
        </div>
      </Shell>
    );
  }

  if (statisticsError || !statistics) {
    return (
      <Shell
        channel={shape.channelName}
        title={title}
        subtitle={subtitle}
        status={null}
        onClose={onClose}
        footer={closeButton}
      >
        <div className="flex h-56 flex-col items-center justify-center gap-3">
          <XCircleIcon className="h-8 w-8 text-red-500" />
          <p className="text-sm text-slate-600">Couldn't load this campaign's report.</p>
          <button
            onClick={fetchBulkCampaignStatistics}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  const responseByAttempt = statistics.responseByAttempt || {};
  const extractionBreakdown = statistics.extractionBreakdown || {};
  const extractedFields = Object.keys(extractionBreakdown);
  const hasExtractions = extractedFields.length > 0;

  /*
   * Resolved rather than stored: falls back to the first field whenever the current selection
   * isn't in this campaign's field list. That covers both the initial render and the case where
   * the modal is reopened on a different campaign whose extraction parameters don't overlap —
   * without it the breakdown would silently render nothing for a field this campaign never had.
   */
  const activeField =
    selectedField && extractedFields.includes(selectedField)
      ? selectedField
      : extractedFields[0];

  /*
   * Values sorted by count, largest first. The backend hands these back in whatever order the
   * aggregation produced, which puts no useful value at the top; for a breakdown the reader is
   * scanning for the dominant answer, so frequency order is what makes it readable at a glance.
   */
  const activeFieldValues = Object.entries(extractionBreakdown[activeField] || {})
    .map(([value, count]) => ({ value, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);

  /*
   * Attempt keys arrive as strings on a JSON object, whose property order is not something to
   * rely on for numeric keys, so they are sorted explicitly — otherwise attempt 10 would sit
   * between 1 and 2. Non-numeric keys sort to the end rather than poisoning the comparator
   * with NaN, which would leave the whole axis in an arbitrary order.
   */
  const attempts = Object.entries(responseByAttempt)
    .map(([attempt, count]) => ({
      label: `Attempt ${attempt}`,
      value: Number(count) || 0,
      order: Number.isFinite(Number(attempt)) ? Number(attempt) : Number.POSITIVE_INFINITY,
    }))
    .sort((a, b) => a.order - b.order);

  const hasAttempts = attempts.length > 0;

  return (
    <Shell
      channel={shape.channelName}
      title={title}
      subtitle={subtitle}
      status={statistics.status}
      onClose={onClose}
      footer={
        <>
          {closeButton}
          {/*
            * A running campaign's figures move while the modal is open, and follow-upsScheduled
            * is live even on a frozen report — so there has to be a way to re-read without
            * closing and reopening. mr-auto pushes it away from the two right-aligned actions:
            * it reloads the view rather than doing something to the campaign.
            */}
          <button
            onClick={fetchBulkCampaignStatistics}
            className="mr-auto flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            Refresh
          </button>
          <button
            onClick={() => onExport?.(bulkCampaign)}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            <PaperAirplaneIcon className="h-3.5 w-3.5" />
            Export Report
          </button>
        </>
      }
    >
      {/* Top-line numbers */}
      <div className="mb-6 flex items-center justify-between rounded-xl bg-slate-50 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
            <UsersIcon className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Total Contacts</p>
            <p className="text-xl font-semibold text-slate-900">
              {(statistics.totalContacts || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/*
          * Hidden while the campaign is still running. The rate divides by calls *attempted*,
          * not by contacts, so mid-flight both sides of it are still moving and the figure is
          * not a partial result heading for the final one — it swings as the mix of outcomes
          * changes. Showing it would read as "this is how the campaign is doing"; omitting it
          * says the number isn't answerable yet, which is the honest version.
          *
          * Read off statistics.status, not bulkCampaign.status: the latter comes from the row
          * that opened the modal and can be stale if the campaign finished since the list was
          * loaded. statistics.status is what the header badge renders, so the tile and the
          * badge can never disagree.
          */}
        {statistics.status !== BULK_CAMPAIGN_STATUS.ONGOING && (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <SignalIcon className="h-[18px] w-[18px]" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{shape.rateLabel}</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatRate(statistics.connectivityRate)}
              </p>
            </div>
          </div>
        )}

        {/*
          * Two gates, for two different reasons.
          *
          * Numeric, not truthy: 0 is a real answer — nothing left to dial — whereas an absent
          * field means the backend either predates this count or failed to run it, and has
          * nothing to report. Showing "0" for those would assert something we never measured.
          *
          * Hidden once Completed: a bulk campaign only reaches that status after the processor
          * confirms no follow-ups remain, so the count is necessarily 0 and the tile just
          * restates the status badge. Worse, the count is live rather than frozen, so a
          * late-arriving follow-up could put a non-zero number next to a "Completed" badge and
          * read as a contradiction.
          */}
        {typeof statistics.followUpsScheduled === "number" &&
          statistics.status !== BULK_CAMPAIGN_STATUS.COMPLETED && (
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <CalendarDaysIcon className="h-[18px] w-[18px]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Follow-ups Scheduled</p>
                <p className="text-xl font-semibold text-slate-900">
                  {statistics.followUpsScheduled.toLocaleString()}
                </p>
              </div>
            </div>
          )}
      </div>

      {/* Chart + stat cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-5">
        <div className="sm:col-span-2">
          {ringTotal > 0 ? (
            <>
              <div className="h-40 w-full">
                <PieChart breakdown={breakdown} />
              </div>
              {/* A pie has no hole to print the total in, so it sits underneath. It still has
                  to state what the arcs actually add up to: normally that is totalContacts,
                  but when outcomes overrun it the slices are drawn in proportion to their
                  sum, so printing the contact count would contradict the chart above it. */}
              <p className="mt-2 text-center text-[11px] text-slate-400">
                {ringTotal.toLocaleString()} {overCounted ? "outcomes" : "contacts"}
              </p>
              {/* Values are printed, not just hued: the slice colours sit under 3:1 against
                  white, so the legend has to carry identity and magnitude on its own. */}
              <div className="mt-2 flex flex-col gap-1">
                {breakdown.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-1.5 text-slate-500">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      {entry.name}
                    </span>
                    <span className="font-medium text-slate-900">
                      {entry.value.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              {overCounted && (
                <p className="mt-2 text-[11px] text-amber-600">
                  Outcomes exceed the contact count — some contacts were reached more than once.
                </p>
              )}
            </>
          ) : (
            // Chart.js draws nothing for an all-zero dataset, which reads as a broken chart
            // rather than an untouched campaign.
            <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-200 px-4 text-center">
              <p className="text-xs text-slate-400">No activity recorded yet</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2.5 sm:col-span-3 sm:grid-cols-2">
          {shape.cards.map(({ key, label, icon: Icon, tone }) => (
            /*
             * Label above value rather than beside it. Side-by-side only fitted while every
             * label happened to contain a space to wrap on: the card is ~177px wide, of which
             * the icon and padding take ~76px, so a single long word ("Unanswered") filled the
             * row and collided with the number. Stacking removes the shared row entirely, so no
             * label length can reproduce it — and it matches the tiles at the top of this modal.
             */
            <div
              key={key}
              className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3"
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toneStyle(tone)}`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-500">{label}</p>
                <p className="text-sm font-semibold text-slate-900">
                  {(statistics.campaignStatistics?.[key] || 0).toLocaleString()}
                </p>
              </div>
            </div>
          ))}

          {/* Chat campaigns always report 0 minutes, so this would be dead weight there. */}
          {shape.showsDuration && (
            // Same stacked layout as the mapped cards above — it sits in the same grid, and
            // "Total Minutes" is the other label long enough to wrap beside its value.
            <div className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toneStyles.neutral}`}
              >
                <ClockIcon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-500">Total Minutes</p>
                <p className="text-sm font-semibold text-slate-900">
                  {(statistics.totalMinutes || 0).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {hasAttempts && (
        <div className="mt-6">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Responses by attempt
          </p>
          {/*
            * Scrolls inside its own container so a long campaign can't widen the modal or make
            * the page scroll sideways. The inner element keeps w-full so a two-attempt campaign
            * still fills the width instead of leaving a stub chart against the left edge.
            */}
          <div className="overflow-x-auto">
            <div
              className="h-44 w-full"
              style={{ minWidth: `${attempts.length * MIN_BAR_SLOT}px` }}
            >
              <AttemptBarChart attempts={attempts} />
            </div>
          </div>
        </div>
      )}

      {hasExtractions && (
        <div className="mt-6">
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Extracted fields
            </p>

            {/*
              * A native select rather than tabs or chips: the number of extraction parameters is
              * set per assistant and unbounded, so a horizontal control would either overflow or
              * wrap into several rows. With one field there is nothing to choose between, so the
              * name is shown as plain text instead of a one-option dropdown.
              */}
            {extractedFields.length > 1 ? (
              <Select
                aria-label="Extracted field"
                value={activeField}
                // Typed structurally on purpose: tsconfig sets lib to ES2023 with no DOM, so
                // HTMLSelectElement doesn't exist here, and Select comes from an untyped .jsx
                // so the parameter would otherwise be an implicit any.
                onChange={(e: { target: { value: string } }) => setSelectedField(e.target.value)}
                className="!w-auto max-w-[60%]"
              >
                {extractedFields.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="truncate text-sm font-medium text-slate-700">{activeField}</p>
            )}
          </div>

          <div className="rounded-xl border border-slate-100 px-4 py-3">
            {activeFieldValues.length > 0 ? (
              /*
               * Scrolls within itself rather than growing. A free-text extraction parameter can
               * come back with as many distinct values as there were contacts, which would push
               * everything below it out of reach and leave the modal's own scrollbar as the only
               * way back. The cap keeps the section a predictable height whatever the field holds.
               * pr-1 leaves the scrollbar its own gutter instead of overlapping the counts.
               */
              <div className="flex max-h-44 flex-col gap-1.5 overflow-y-auto pr-1">
                {activeFieldValues.map(({ value, count }) => (
                  <div key={value} className="flex items-baseline justify-between gap-3 text-xs">
                    {/* Extracted values are free text from the model, so they can be long or
                        empty — break rather than push the count out of the row. */}
                    <span className="min-w-0 break-words text-slate-500">
                      {value || "—"}
                    </span>
                    <span className="shrink-0 font-medium text-slate-900">
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No values extracted for this field yet</p>
            )}
          </div>
        </div>
      )}
    </Shell>
  );
}

export default BulkCampaignReportModal;
